import React, { Component } from 'react'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import 'react-datepicker/dist/react-datepicker.css'
import './ConsentFlow/ConsentFlow.scss'
import { withRouting } from '../common/hooks'
import { connect } from 'react-redux'
import { signOutUser } from '../../actions/authActions'
import { FAMILY_ADMIN_MIN_AGE } from '../../misc/Constants'
import PropTypes from 'prop-types'

const CONSENT_NAMES = {
  privacy_policy: 'Privacy Policy',
  eula: 'Terms of Use',
  saas_agreement: 'Coach End-User Licensing Agreement',
}

const FAMILY_CONSENT_NAMES = {
  privacy_policy: 'Privacy Policy',
  eula: 'Terms of Use',
  coppa_policy: 'COPPA Policy',
}

class ConsentContainer extends Component {
  state = {
    allConsentCheckbox: false,
    over12Checkbox: false,
    submitting: false,
    birthdate: moment(),
    birthdateError: null,
    familyAgeSkipped: false,
    familyAges: {},
    loadingFamilyAges: true,
    familyConsentsSkipped: false,
    needsPersonalConsent: false,
    familyConsentsCheckbox: false,
    submittingFamily: false,
  }

  componentDidMount() {
    this._initializeFamilyAgesObj()
    this._checkPersonalConsents()
  }

  // eslint-disable-next-line max-lines-per-function, complexity
  render() {
    const { consents, consentType } = this.props

    if (!consents) {
      return null
    }

    const {
      allConsentCheckbox,
      over12Checkbox,
      submitting,
      birthdate,
      birthdateError,
      familyAgeSkipped,
      familyAges,
      loadingFamilyAges,
      familyConsentsSkipped,
      familyConsentsCheckbox,
      submittingFamily,
      needsPersonalConsent,
    } = this.state

    if (consentType === 'userAge') {
      return (
        <div id='ConsentContainer'>
          <h2>Please add your birth date to continue</h2>

          <p className='subtext'>
            Providing your birth date lets us know who is old enough to use the Op 36 App without parental supervision and who can allow their&nbsp;
            younger family members to use the app. Learn more about our age requirements in our&nbsp;
            <a href='https://operation36.golf/legal/' target='_blank' rel='noopener noreferrer'>
              Terms of Use
            </a>
            &nbsp;and&nbsp;
            <a href='https://operation36.golf/legal/' target='_blank' rel='noopener noreferrer'>
              Privacy Policy
            </a>
            .
          </p>

          <p className='bold mb-0'>Input your birth date here:</p>

          <DatePicker
            selected={birthdate.toDate()}
            onChange={this._handleBirthdateChange}
          />

          {birthdateError && <p className='error'>{birthdateError}</p>}

          <button
            onClick={this._submitOwnBirthdateChange}
            className='btn-rectangular btn-rectangular-main-positive mt-4'
            disabled={!birthdate}
          >
            Continu{submitting ? 'ing' : 'e'}
          </button>
        </div>
      )
    }

    if (consentType === 'contactSupport') {
      return (
        <div id='ConsentContainer'>
          <h2>You must be at least 13 years old to be a family admin</h2>

          <p className='subtext'>
            You must be at least 13 years old to be a family admin in this app. Because you are under 13 and there are no other admins in your&nbsp;
            family, your account has been disabled for now. If you believe that this was a mistake, please contact us at&nbsp;
            <span className='#ff6633'>support@op36golf.com</span> so we can help
            you.
          </p>
          <button onClick={this._signOut} className='btn-rectangular btn-rectangular-main-positive mt-4'>
            Log Out
          </button>
        </div>
      )
    }

    if (consentType === 'needsProxy') {
      return (
        <div id='ConsentContainer'>
          <h2>Please ask one of your family admins to consent to our updated legal agreements</h2>

          <p className='subtext'>
            To continue using Operation 36, please ask one of the family admins in your family to log in and consent to our updated legal agreements.
          </p>

          <p className='bold mb-0'>Ask one of these family members:</p>
          <ul>
            {consents.needs_proxy.map((fam_ad, i) => (
              <li key={i}>{fam_ad}</li>
            ))}
          </ul>
          <button onClick={this._signOut} className='btn-rectangular btn-rectangular-main-positive mt-4'>
            Log Out
          </button>
        </div>
      )
    }

    if (consents.family_ages_needed && consents.family_ages_needed.length > 0 && !familyAgeSkipped) {
      return (
        <div id='ConsentContainer'>
          <h2>Please add birthdays for these family members</h2>
          <p className='subtext'>
            Providing their birthdays lets us know who is old enough to use the
            Op 36 App without parental supervision and who can allow their
            younger family members to use the app. Learn more about our age
            requirements in&nbsp;
            <a
              href='https://operation36.golf/legal/'
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms of Use
            </a>
            &nbsp;and&nbsp;
            <a
              href='https://operation36.golf/legal/'
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy Policy
            </a>
            .
          </p>
          <p className='bold mb-0'>Input birthdays:</p>
          {loadingFamilyAges ? (
            <i className='fa fa-spinner fa-spin' aria-hidden='true' />
          ) : (
            consents.family_ages_needed.map((usr) => (
              <div
                className='d-flex align-items-center justify-content-between'
                key={usr[0]}
              >
                <p className='mb-0'>{familyAges[usr[0]].name}</p>
                <DatePicker
                  selected={familyAges[usr[0]].birthdate}
                  onChange={(data) =>
                    this._handleFamilyBirthdateChange(usr[0], data)
                  }
                />
              </div>
            ))
          )}
          <div className='d-flex mt-4'>
            <button
              onClick={this._skipFamilyAges}
              className='btn-rectangular btn-rectangular-main-negative mt-4'
            >
              Skip
            </button>
            <button
              onClick={this._submitFamilyAges}
              className='btn-rectangular btn-rectangular-main-positive mt-4'
              disabled={this._anyMissingFamilyAges()}
            >
              Submit
            </button>
          </div>
        </div>
      )
    }

    if (consents.family_consents_needed && consents.family_consents_needed.length > 0 && !familyConsentsSkipped) {
      return (
        <div id='ConsentContainer'>
          <h2>
            Please consent to our updated legal agreements for these family
            members
          </h2>
          <p className='subtext'>
            Some of your family members are under 13, so they need you to
            consent to these agreements on their behalf in order for them to use
            the Op 36 App. Please click on the links below to review the
            agreements before continuing.
          </p>
          <p className='bold mb-0'>Consent required for:</p>
          <ul>{this._renderFamilyConsentLinks()}</ul>

          <div className='checkbox-container mt-2'>
            <div
              role='checkbox'
              aria-checked={familyConsentsCheckbox}
              tabIndex={0}
              className={`checkbox ${familyConsentsCheckbox ? 'filled' : ''}`}
              onClick={() => this._handleCheckbox('familyConsentsCheckbox')}
              onKeyDown={() => this._handleCheckbox('familyConsentsCheckbox')}
            >
              {familyConsentsCheckbox && (
                <i className='fa fa-check' aria-hidden='true' />
              )}
            </div>
            <span>
              By checking here and clicking &quot;Continue&quot; below, you agree to
              consent on behalf of the users listed above.
            </span>
          </div>
          <div className='d-flex'>
            <button
              onClick={this._skipFamilyConsents}
              className='btn-rectangular btn-rectangular-main-negative mt-4'
            >
              Skip
            </button>
            <button
              onClick={this._submitFamilyConsent}
              className='btn-rectangular btn-rectangular-main-positive mt-4'
              disabled={!familyConsentsCheckbox || submittingFamily}
            >
              Continu{submittingFamily ? 'ing' : 'e'}
            </button>
          </div>
        </div>
      )
    }

    if (needsPersonalConsent) {
      return (
        <div id='ConsentContainer'>
          <h2>
            Please consent to the latest terms and conditions in the documents below before continuing
          </h2>
          <p className='subtext'>
            You are seeing this because you are either new to Operation36, or we have updated the documents liseted below. Please click on the&nbsp;
            links below to review these documents before continuing.
          </p>

          <p className='bold mb-0'>Consent required for:</p>
          <ul>{this._renderConsentLinks()}</ul>

          <div className='checkbox-container'>
            <div
              role='checkbox'
              aria-checked={over12Checkbox}
              tabIndex={0}
              className={`checkbox ${over12Checkbox ? 'filled' : ''}`}
              onClick={() => this._handleCheckbox('over12Checkbox')}
              onKeyDown={() => this._handleCheckbox('over12Checkbox')}
            >
              {over12Checkbox && (
                <i className='fa fa-check' aria-hidden='true' />
              )}
            </div>
            <span>I agree that I am 13 years of age or older.</span>
          </div>
          <div className='checkbox-container mt-2'>
            <div
              role='checkbox'
              aria-checked={allConsentCheckbox}
              tabIndex={0}
              className={`checkbox ${allConsentCheckbox ? 'filled' : ''}`}
              onClick={() => this._handleCheckbox('allConsentCheckbox')}
              onKeyDown={() => this._handleCheckbox('allConsentCheckbox')}
            >
              {allConsentCheckbox && (
                <i className='fa fa-check' aria-hidden='true' />
              )}
            </div>
            <span>
              By checking here and clicking &quot;Continue&quot; below, you agree to be
              bound by the terms and conditions of the documents listed above.
            </span>
          </div>
          <button
            onClick={this._submitConsent}
            className='btn-rectangular btn-rectangular-main-positive mt-4'
            disabled={!over12Checkbox || !allConsentCheckbox || submitting}
          >
            Continu{submitting ? 'ing' : 'e'}
          </button>
        </div>
      )
    }

    if (familyAgeSkipped || familyConsentsSkipped) {
      return this._shutHerDown()
    }

    return null
  }

  _shutHerDown = () => {
    // they skipped all the family stuff and don't have personal consents
    this.props.closeModal()
    return null
  }

  _renderFamilyConsentLinks = () => {
    const { consents } = this.props
    return consents.family_consents_needed.map((usr) => (
      <li key={usr.id}>
        {usr.name}
        <ul>{this._renderFamilyIndividualConsents(usr)}</ul>
      </li>
    ))
  }

  _renderFamilyIndividualConsents = (usr) => {
    const consentsNeeded = []
    Object.keys(FAMILY_CONSENT_NAMES).forEach((consent) => {
      if (!usr[consent]) {
        consentsNeeded.push(consent)
      }
    })
    return consentsNeeded.map((consent, idx) => (
      <li key={idx}>
        <a
          href='https://operation36.golf/legal/'
          target='_blank'
          rel='noopener noreferrer'
        >
          {FAMILY_CONSENT_NAMES[consent]}
        </a>
      </li>
    ))
  }

  _checkPersonalConsents = () => {
    const { consents } = this.props
    let ret = false
    for (const [consentName, consentData] of Object.entries(consents)) {
      if (Object.prototype.hasOwnProperty.call(CONSENT_NAMES, consentName)) {
        if (!consentData.consented) {
          ret = true
        }
      }
    }

    this.setState({ needsPersonalConsent: ret })
  }

  _submitFamilyConsent = () => {
    this.setState({ submittingFamily: true }, () => {
      this.props.submitFamilyConsents()
    })
  }

  _submitFamilyAges = () => {
    const { submitFamilyBirthdates } = this.props
    const { familyAges } = this.state
    const userArray = []
    for (const [id, data] of Object.entries(familyAges)) {
      if (Object.prototype.hasOwnProperty.call(familyAges, id)) {
        userArray.push({ id: id, birthdate: data.birthdate })
      }
    }
    submitFamilyBirthdates(userArray)
  }

  _skipFamilyConsents = () => {
    this.setState({ familyConsentsSkipped: true })
  }

  _skipFamilyAges = () => {
    this.setState({ familyAgeSkipped: true })
  }

  _anyMissingFamilyAges = () => {
    const { familyAges, loadingFamilyAges } = this.state
    if (loadingFamilyAges) {
      return true
    }
    let ret = false
    for (const property in familyAges) {
      if (Object.prototype.hasOwnProperty.call(familyAges, property)) {
        if (!familyAges[property].birthdate) {
          ret = true
        }
      }
    }
    return ret
  }

  _handleFamilyBirthdateChange = (id, date) => {
    const { familyAges } = this.state
    const familyAgesCopy = Object.assign({}, familyAges)
    familyAgesCopy[id].birthdate = date
    this.setState({ familyAges: familyAgesCopy })
  }

  _initializeFamilyAgesObj = () => {
    const { consents } = this.props
    const { familyAges } = this.state
    if (
      !consents.family_ages_needed ||
      consents.family_ages_needed.length === 0
    ) {
      return
    }
    const newFamilyObj = Object.assign({}, familyAges)
    consents.family_ages_needed.forEach((usr) => {
      newFamilyObj[usr[0]] = { name: `${usr[1]} ${usr[2]}`, birthdate: null }
    })
    this.setState({ familyAges: newFamilyObj, loadingFamilyAges: false })
  }

  _isFamilyAdminUnderMinAge = () => {
    const { currentUser } = this.props
    const { birthdate } = this.state

    return Boolean(currentUser?.family_admin) && moment().diff(birthdate, 'years') < FAMILY_ADMIN_MIN_AGE
  }

  _submitOwnBirthdateChange = () => {
    const { birthdate } = this.state
    const { submitOwnBirthdate } = this.props

    if (this._isFamilyAdminUnderMinAge()) {
      this.setState({ birthdateError: `You must be at least ${FAMILY_ADMIN_MIN_AGE} years old` })
      return
    }

    submitOwnBirthdate(birthdate)
  }

  _submitConsent = () => {
    const { consents, submitConsents } = this.props
    const { allConsentCheckbox, over12Checkbox, submitting } = this.state
    if (!over12Checkbox || !allConsentCheckbox || submitting) {
      return false
    }
    this.setState({ submitting: true }, () => {
      const updatedConsents = this._getConsentsNeeded(consents)
      submitConsents(updatedConsents)
    })
  }

  _handleBirthdateChange = (date) => {
    this.setState({ birthdate: moment(date), birthdateError: null })
  }

  _handleCheckbox = (val) => {
    this.setState((prevState) => ({ [val]: !prevState[val] }))
  }

  _renderConsentLinks = () => {
    const { consents } = this.props
    const consentsNeeded = this._getConsentsNeeded(consents)
    return consentsNeeded.map((c, i) => (
      <li key={i}>
        <a
          href='https://operation36.golf/legal/'
          target='_blank'
          rel='noopener noreferrer'
        >
          {CONSENT_NAMES[c.name]}
        </a>
      </li>
    ))
  }

  _getConsentsNeeded = (consents) => {
    const consentArray = []
    for (const [consentName, consentData] of Object.entries(consents)) {
      if (Object.prototype.hasOwnProperty.call(CONSENT_NAMES, consentName)) {
        if (!consentData.consented) {
          consentArray.push({
            name: consentName,
            version: consentData.version,
          })
        }
      }
    }
    return consentArray
  }

  _signOut = () => {
    this.props.dispatch(signOutUser())
  }
}

ConsentContainer.propTypes = {
  navigate: PropTypes.func,
  dispatch: PropTypes.func,
  currentUser: PropTypes.object,
  consents: PropTypes.object,
  consentType: PropTypes.string,
  closeModal: PropTypes.func,
  submitConsents: PropTypes.func,
  submitFamilyConsents: PropTypes.func,
  submitFamilyBirthdates: PropTypes.func,
  submitOwnBirthdate: PropTypes.func,
}

export default withRouting(connect()(ConsentContainer))
